import { render, screen } from "@testing-library/react";
import {
  CanvasAggregateWidget,
  CanvasWidgetDataContext,
  requestFor,
} from "./CanvasAggregateWidget";
import { useViewAggregateQuery } from "../../../hooks/useViewAggregateQuery";
import type { RenderableElement } from "../CanvasRenderer";
import { CanvasRuntimeProvider } from "../CanvasRuntimeContext";

jest.mock("../../../hooks/useViewAggregateQuery", () => ({
  useViewAggregateQuery: jest.fn(),
}));

// Expose the chart data as JSON so the tests can read the datasets the widget builds.
jest.mock("react-chartjs-2", () => ({
  Bar: (props: { data: unknown; options: unknown }) => (
    <div data-testid="bar" data-chart={JSON.stringify(props)} />
  ),
  Line: (props: { data: unknown; options: unknown }) => (
    <div data-testid="line" data-chart={JSON.stringify(props)} />
  ),
}));

const element: RenderableElement = {
  id: "metric-1",
  type: "widget",
  props: {
    label: "Headcount",
    widget_type: "METRIC",
    data_source_alias: "main",
    metric_config: {
      value_field: "id",
      aggregation: "count",
      format: "number",
    },
  },
  gridColumn: 1,
  gridRow: 1,
  columnSpan: 3,
  rowSpan: 2,
};

describe("chart typography", () => {
  function chartWith(settings: Record<string, unknown>) {
    (useViewAggregateQuery as jest.Mock).mockReturnValue({
      isLoading: false,
      isError: false,
      data: {
        current: [
          {
            keys: { month: "September" },
            values: { series_0: 49, series_1: 2 },
          },
        ],
        comparison: [],
      },
    });
    render(
      <CanvasWidgetDataContext.Provider
        value={[
          {
            id: "main",
            name: "main",
            type: "view",
            view_id: "view-1",
            config: {},
          },
        ]}
      >
        <CanvasAggregateWidget
          mode="runtime"
          element={{
            ...element,
            props: {
              widget_type: "CHART",
              data_source_alias: "main",
              chart_config: {
                chart_type: "bar",
                x_field: "month",
                y_fields: ["active", "hires"],
                aggregation: "sum",
                ...settings,
              },
            },
          }}
        />
      </CanvasWidgetDataContext.Provider>,
    );
    return JSON.parse(
      screen.getByTestId("bar").getAttribute("data-chart") || "{}",
    );
  }

  it("applies bounded legend and axis font settings without changing labels or values", () => {
    const chart = chartWith({ legend_font_size: 10, axis_font_size: 11 });
    expect(chart.options.plugins.legend.labels.font.size).toBe(10);
    expect(chart.options.scales.x.ticks.font.size).toBe(11);
    expect(chart.options.scales.y.ticks.font.size).toBe(11);
    expect(chart.data.labels).toEqual(["September"]);
    expect(
      chart.data.datasets.map((d: { label: string; data: number[] }) => [
        d.label,
        d.data,
      ]),
    ).toEqual([
      ["active", [49]],
      ["hires", [2]],
    ]);
  });

  it.each([
    {},
    { legend_font_size: 0, axis_font_size: 999 },
    { legend_font_size: "10", axis_font_size: NaN },
  ])(
    "keeps chart defaults for missing or invalid typography %j",
    (settings) => {
      const chart = chartWith(settings);
      expect(chart.options.plugins.legend.labels?.font).toBeUndefined();
      expect(chart.options.scales?.x?.ticks?.font).toBeUndefined();
      expect(chart.options.plugins.legend.display).toBe(true);
    },
  );
});

it("queries the full view aggregate and renders a metric", () => {
  (useViewAggregateQuery as jest.Mock).mockReturnValue({
    isLoading: false,
    isError: false,
    data: { current: [{ keys: {}, values: { value: 1502 } }], comparison: [] },
  });
  render(
    <CanvasWidgetDataContext.Provider
      value={[
        {
          id: "main",
          name: "main",
          type: "view",
          view_id: "view-1",
          config: {},
        },
      ]}
    >
      <CanvasAggregateWidget element={element} mode="runtime" />
    </CanvasWidgetDataContext.Provider>,
  );
  expect(useViewAggregateQuery).toHaveBeenCalledWith(
    "view-1",
    {
      group_by: [],
      measures: [{ key: "value", aggregation: "count", field: undefined }],
    },
    true,
    undefined,
  );
  expect(screen.getByText("1,502")).toBeTruthy();
});

it("sends runtime canvas context with aggregate widgets", () => {
  (useViewAggregateQuery as jest.Mock).mockReturnValue({ isLoading: true });
  render(
    <CanvasRuntimeProvider
      value={{
        workspaceId: "ws",
        canvasId: "canvas-1",
        rowId: null,
        rowData: null,
      }}
    >
      <CanvasWidgetDataContext.Provider
        value={[
          {
            id: "main",
            name: "main",
            type: "view",
            view_id: "view-1",
            config: {},
          },
        ]}
      >
        <CanvasAggregateWidget element={element} mode="runtime" />
      </CanvasWidgetDataContext.Provider>
    </CanvasRuntimeProvider>,
  );
  expect(useViewAggregateQuery).toHaveBeenLastCalledWith(
    "view-1",
    expect.anything(),
    true,
    "canvas-1",
  );
});

it("builds one aggregate measure per chart series", () => {
  expect(
    requestFor({
      widget_type: "CHART",
      chart_config: {
        chart_type: "line",
        x_field: "month",
        y_fields: ["headcount", "hires"],
        aggregation: "sum",
      },
    }),
  ).toEqual({
    group_by: ["month"],
    measures: [
      { key: "series_0", aggregation: "sum", field: "headcount" },
      { key: "series_1", aggregation: "sum", field: "hires" },
    ],
  });
});

describe("chart series_field", () => {
  const seriesChart = (over: Record<string, unknown> = {}) => ({
    widget_type: "CHART" as const,
    chart_config: {
      chart_type: "bar",
      x_field: "month",
      series_field: "region",
      y_fields: ["employees"],
      aggregation: "sum",
      ...over,
    },
  });

  it("asks the API to group by the series field as well", () => {
    expect(requestFor(seriesChart())).toEqual({
      group_by: ["month"],
      series_field: "region",
      measures: [{ key: "series_0", aggregation: "sum", field: "employees" }],
    });
  });

  it("does not send a series field when the chart has none", () => {
    expect(requestFor(seriesChart({ series_field: undefined }))).toEqual({
      group_by: ["month"],
      measures: [{ key: "series_0", aggregation: "sum", field: "employees" }],
    });
  });

  it("treats a series field equal to the x field as an unconfigured chart", () => {
    expect(requestFor(seriesChart({ series_field: "month" }))).toBeNull();
  });

  it("renders one dataset per series value across two months and two regions", () => {
    (useViewAggregateQuery as jest.Mock).mockReturnValue({
      isLoading: false,
      isError: false,
      data: {
        current: [
          {
            keys: { month: "2026-07-01", region: "Europe" },
            values: { series_0: 95 },
          },
          {
            keys: { month: "2026-07-01", region: "US" },
            values: { series_0: 190 },
          },
          {
            keys: { month: "2026-08-01", region: "Europe" },
            values: { series_0: 100 },
          },
          {
            keys: { month: "2026-08-01", region: "US" },
            values: { series_0: 201 },
          },
        ],
        comparison: [],
      },
    });
    render(
      <CanvasWidgetDataContext.Provider
        value={[
          {
            id: "main",
            name: "main",
            type: "view",
            view_id: "view-1",
            config: {},
          },
        ]}
      >
        <CanvasAggregateWidget
          element={{
            ...element,
            id: "chart-1",
            props: {
              title: "Employees by month and region",
              data_source_alias: "main",
              ...seriesChart(),
            },
          }}
          mode="runtime"
        />
      </CanvasWidgetDataContext.Provider>,
    );
    const chart = JSON.parse(
      screen.getByTestId("bar").getAttribute("data-chart") || "{}",
    );
    expect(chart.data.labels).toEqual(["2026-07-01", "2026-08-01"]);
    expect(
      chart.data.datasets.map((set: { label: string; data: unknown[] }) => [
        set.label,
        set.data,
      ]),
    ).toEqual([
      ["Europe", [95, 100]],
      ["US", [190, 201]],
    ]);
    expect(chart.options.plugins.legend.display).toBe(true);
  });

  it("leaves a series value without a point in a month empty instead of zero", () => {
    (useViewAggregateQuery as jest.Mock).mockReturnValue({
      isLoading: false,
      isError: false,
      data: {
        current: [
          {
            keys: { month: "2026-07-01", region: "US" },
            values: { series_0: 190 },
          },
          {
            keys: { month: "2026-08-01", region: "Europe" },
            values: { series_0: 100 },
          },
          {
            keys: { month: "2026-08-01", region: "US" },
            values: { series_0: 201 },
          },
        ],
        comparison: [],
      },
    });
    render(
      <CanvasWidgetDataContext.Provider
        value={[
          {
            id: "main",
            name: "main",
            type: "view",
            view_id: "view-1",
            config: {},
          },
        ]}
      >
        <CanvasAggregateWidget
          element={{
            ...element,
            id: "chart-2",
            props: { data_source_alias: "main", ...seriesChart() },
          }}
          mode="runtime"
        />
      </CanvasWidgetDataContext.Provider>,
    );
    const chart = JSON.parse(
      screen.getByTestId("bar").getAttribute("data-chart") || "{}",
    );
    expect(
      chart.data.datasets.map((set: { label: string; data: unknown[] }) => [
        set.label,
        set.data,
      ]),
    ).toEqual([
      ["US", [190, 201]],
      ["Europe", [null, 100]],
    ]);
  });
});
